import clsx from 'clsx'

interface ProbabilityDisplayProps {
  probability: number | null
  confidence: number | null
  large?: boolean
  showGauge?: boolean
}

function getVerdict(probability: number) {
  if (probability >= 0.7) return { text: 'LIKELY YES', color: 'text-signal-green', stroke: '#22c55e' }
  if (probability <= 0.3) return { text: 'LIKELY NO', color: 'text-signal-red', stroke: '#ef4444' }
  return { text: 'UNCERTAIN', color: 'text-signal-amber', stroke: '#f59e0b' }
}

function ProbabilityGauge({ probability, size = 120 }: { probability: number; size?: number }) {
  const verdict = getVerdict(probability)
  const strokeWidth = size * 0.08
  const radius = (size - strokeWidth) / 2
  const circumference = Math.PI * radius // Half circle
  const progress = probability * circumference

  return (
    <div className="relative" style={{ width: size, height: size * 0.6 }}>
      <svg
        width={size}
        height={size * 0.6}
        viewBox={`0 0 ${size} ${size * 0.6}`}
        className="overflow-visible"
      >
        {/* Background arc */}
        <path
          d={`M ${strokeWidth / 2} ${size * 0.55} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size * 0.55}`}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-star-dim/20"
          strokeLinecap="round"
        />
        {/* Progress arc */}
        <path
          d={`M ${strokeWidth / 2} ${size * 0.55} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${size * 0.55}`}
          fill="none"
          stroke={verdict.stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={`${progress} ${circumference}`}
          className="transition-all duration-700 ease-out"
        />
        {/* Tick marks */}
        {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
          const angle = Math.PI * (1 - tick)
          const innerR = radius - strokeWidth * 0.8
          const outerR = radius + strokeWidth * 0.8
          const cx = size / 2
          const cy = size * 0.55
          return (
            <line
              key={tick}
              x1={cx + innerR * Math.cos(angle)}
              y1={cy - innerR * Math.sin(angle)}
              x2={cx + outerR * Math.cos(angle)}
              y2={cy - outerR * Math.sin(angle)}
              stroke="currentColor"
              strokeWidth={tick === 0.5 ? 2 : 1}
              className={tick === 0.5 ? 'text-star-dim/40' : 'text-star-dim/20'}
            />
          )
        })}
      </svg>
      {/* Needle */}
      <div
        className="absolute left-1/2 transition-transform duration-700 ease-out"
        style={{
          bottom: size * 0.05,
          transformOrigin: 'bottom center',
          transform: `translateX(-50%) rotate(${-90 + probability * 180}deg)`,
        }}
      >
        <div
          className="w-0.5 rounded-full"
          style={{
            height: radius * 0.7,
            background: `linear-gradient(to top, ${verdict.stroke}, ${verdict.stroke}88)`,
          }}
        />
      </div>
      {/* Center dot */}
      <div
        className="absolute left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-star-dim"
        style={{ bottom: size * 0.05 - 4 }}
      />
    </div>
  )
}

function MiniBar({ probability }: { probability: number }) {
  const verdict = getVerdict(probability)
  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex-1 h-1.5 bg-star-dim/20 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${probability * 100}%`,
            backgroundColor: verdict.stroke,
          }}
        />
      </div>
      <span className={clsx('font-mono text-xs font-semibold min-w-[3rem] text-right', verdict.color)}>
        {(probability * 100).toFixed(0)}%
      </span>
    </div>
  )
}

export function ProbabilityDisplay({
  probability,
  confidence,
  large = false,
  showGauge = true,
}: ProbabilityDisplayProps) {
  if (probability === null) return <span className="text-star-dim">-</span>

  const probPct = probability * 100
  const confPct = confidence ? confidence * 100 : 50
  const verdict = getVerdict(probability)

  if (large) {
    return (
      <div className="flex flex-col items-center gap-2">
        {showGauge && <ProbabilityGauge probability={probability} size={160} />}
        <div className={clsx('text-4xl font-mono font-bold', verdict.color)}>
          {probPct.toFixed(1)}%
        </div>
        <div className={clsx('text-sm font-mono', verdict.color)}>{verdict.text}</div>
        <div className="flex items-center gap-4 text-xs text-star-dim mt-1">
          <span>Confidence: {confPct.toFixed(0)}%</span>
        </div>
      </div>
    )
  }

  return (
    <MiniBar probability={probability} />
  )
}
